import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import * as jwt from 'jwt-decode';
import { BaseComponent } from './../../../core/components/base/base.component';
import { FeedbackService } from './../../../core/services/feedback.service';
import { GroupService } from './../../../core/services/group.service';
import { UserService } from './../../../core/services/user.service';
import { redirectToLogin } from '../../../core/utils/login-redirect.util';

@Component({
  selector: 'app-accept-invite',
  templateUrl: './accept-invite.component.html',
  styleUrls: ['./accept-invite.component.scss']
})
export class AcceptInviteComponent extends BaseComponent implements OnInit {
  inviteToken?: string;
  isLoading: boolean = true;
  isLoggedIn: boolean = false;

  inviteEmail?: string;
  inviteUserRole?: any;
  /** Set when the server refused the invite for the signed-in account (HTTP 403). */
  errorMessage?: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private groupService: GroupService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.inviteToken = params['token'];
      if (this.validateInviteToken(this.inviteToken!)) {
        this.checkUserAuthorization();
      } else {
        this.showToastMessage('The provided invite token is invalid')
      }
    });
  }

  checkUserAuthorization() {
    this.isLoading = true;
    const sub = this.userService.isLoggedIn().subscribe(
      isLoggedIn => {
        this.isLoggedIn = isLoggedIn;
        this.isLoading = false;
        if (isLoggedIn) {
          this.acceptInvite();
        }
        sub.unsubscribe();
      },
      error => {
        this.isLoading = false;
        console.error('Error checking user authorization:', error);
        sub.unsubscribe();
      }
    );
  }

  validateInviteToken(token: string) {
    try {
      const decodedToken: any = jwt.jwtDecode(token);
      const { email, userRole } = decodedToken;
      this.inviteEmail = email;
      this.inviteUserRole = userRole;
      return true;
    } catch (error: any) {
      console.error('Invalid invite token');
      return false;
    }
  }

  acceptInvite() {
    this.errorMessage = undefined;
    const sub = this.groupService.acceptInvite(this.inviteToken!).subscribe(
      response => {
        this.showToastMessage('Invite accepted successfully.')
        // Handle successful invite acceptance
        this.router.navigate(['/patients', 'schedule']);
        sub.unsubscribe();
      },
      error => {
        const message = error?.error?.message || 'Error accepting invite.';
        console.error('Error accepting invite:', error);
        if (error?.status === 403) {
          // The signed-in account is not the one the invite was addressed to. Stay on this page
          // and say so: a hard redirect to /login would discard the toast before it could be
          // read, and the user needs to know WHICH account to sign in with.
          this.errorMessage = message;
          sub.unsubscribe();
          return;
        }
        this.showToastMessage(message);
        // One navigation to a freshly loaded /login — see `redirectToLogin`.
        redirectToLogin();
        sub.unsubscribe();
      }
    );
  }

  /**
   * Signs the current account out and returns to the login screen with the invite token and the
   * invited address prefilled, so the user can sign in as the invitee and land back here.
   */
  switchAccount() {
    // `logout()` clears localStorage, so the token has to be stashed AFTER it. The login page
    // picks `inviteToken` up from storage on a successful sign-in and comes back to this route.
    this.userService.logout();
    localStorage.setItem('inviteToken', this.inviteToken!);
    redirectToLogin();
  }

  login(registerAccount?: boolean) {
    const role = this.inviteUserRole?.toLowerCase().indexOf('support') > -1 ? 'support' : 'doctor';

    if (registerAccount) {
      this.router.navigate(['/login'], { queryParams: { inviteToken: this.inviteToken, register: true, email: this.inviteEmail, role } });
    } else {
      this.router.navigate(['/login'], { queryParams: { inviteToken: this.inviteToken, email: this.inviteEmail, role } });
    }
  }
}